import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, formatLongDate } from '@structura/i18n';
import './StructuraBits.css';

interface StructuraBitMetadata {
  slug: string;
  title: string;
  readingTime: string;
  date: string;
  thumbnail: string;
  category: string;
  author: string;
  description: string;
}

// Caller passes the app's material so the backend can return only
// material-tagged bits + universal ones for that app. Backend reads the
// `material` query param in StructuraBitsController.List.cs.
export type BitsAppMaterial = 'wood' | 'steel' | 'concrete';

export interface StructuraBitsListProps {
  material: BitsAppMaterial;
}

// The outcome of one (material, language) request, keyed so only the list the page currently
// wants is ever shown: an older response — success or failure — is not the current one.
interface LoadedBits {
  key: string;
  bits: StructuraBitMetadata[];
  error: string;
}

const StructuraBitsList: React.FC<StructuraBitsListProps> = ({ material }) => {
  const { t, language } = useTranslation();
  const requestKey: string = `${material}|${language}`;
  const [loaded, setLoaded] = useState<LoadedBits | null>(null);

  useEffect(() => {
    // One live request per key: a newer request aborts the older one, and a response that still
    // arrives after cleanup is ignored.
    const controller: AbortController = new AbortController();
    let active: boolean = true;
    axios.get<StructuraBitMetadata[]>(`/api/StructuraBits?material=${material}&language=${language}`, { signal: controller.signal })
      .then((response): void => {
        if (active) {
          setLoaded({ key: requestKey, bits: response.data, error: '' });
        }
      })
      .catch((err: unknown): void => {
        if (!active || axios.isCancel(err)) {
          return;
        }
        console.error('Error loading Structura-Bits:', err);
        setLoaded({ key: requestKey, bits: [], error: t('bits.list.errLoad') });
      });
    return (): void => {
      active = false;
      controller.abort();
    };
  }, [material, language, t, requestKey]);

  const current: LoadedBits | null = loaded !== null && loaded.key === requestKey ? loaded : null;
  const loading: boolean = current === null;
  const error: string = current === null ? '' : current.error;
  const bits: StructuraBitMetadata[] = current === null ? [] : current.bits;

  const formatDate = (dateString: string): string => formatLongDate(dateString, language);

  if (loading) {
    return (
      <div className="structura-bits-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('bits.list.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="structura-bits-container">
        <div className="error-message">
          <h2>{t('bits.list.error')}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="structura-bits-container">
      {/* Header */}
      <div className="bits-header">
        <div className="bits-title-section">
          <h1 className="bits-main-title">
            <span className="sparkle-icon">✨</span>
            Structura-Bits
          </h1>
          <p className="bits-subtitle">
            {t('bits.list.subtitle')}
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      {bits.length === 0 ? (
        <div className="no-bits-message">
          <h3>{t('bits.list.noArticles')}</h3>
          <p>{t('bits.list.checkBack')}</p>
        </div>
      ) : (
        <div className="bits-grid">
          {bits.map((bit) => (
            // A real link, so every card is reachable and openable from the keyboard.
            <Link
              key={bit.slug}
              to={`/structura-bits/${bit.slug}`}
              className="bit-card"
              aria-label={bit.title}
            >
              {/* Thumbnail */}
              <div className="bit-thumbnail">
                {bit.thumbnail ? (
                  <img 
                    src={`/api/StructuraBits/images/${bit.thumbnail.split('/').pop()}`}
                    alt={bit.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%234A7C59" opacity="0.12"/%3E%3Ctext x="200" y="150" font-family="sans-serif" font-size="20" fill="%23343f35" text-anchor="middle"%3EStructura-Bit%3C/text%3E%3C/svg%3E';
                    }}
                  />
                ) : (
                  <div className="bit-thumbnail-placeholder">
                    <span>📐</span>
                  </div>
                )}
                <div className="bit-category-badge">{bit.category}</div>
              </div>

              {/* Content */}
              <div className="bit-content">
                <h2 className="bit-title">{bit.title}</h2>
                <p className="bit-description">{bit.description}</p>
                
                <div className="bit-meta">
                  <span className="bit-reading-time">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {bit.readingTime}
                  </span>
                  <span className="bit-date">{formatDate(bit.date)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default StructuraBitsList;

